import {
  ReactElement,
  TextareaHTMLAttributes,
  Ref,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Controller,
  Control,
  FieldValues,
  FieldPath,
  RegisterOptions,
} from "react-hook-form";

import { FormField, FormFieldProps } from "_/components/form-field";

import * as S from "./styled";

type TextAreaProps = FormFieldProps &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "onChange"> & {
    /** True if border should be drawn around element. */
    border?: boolean;

    /** Handler for text changes. */
    onChange?: (value: string) => void;

    /** Sets state as invalid when TextAreaField is being used as a controlled component. */
    invalid?: boolean;
  };

export const TextAreaField = forwardRef(function TextAreaField(
  {
    border = true,
    invalid,
    style,
    className,
    label,
    hint,
    help,
    onChange,
    ...restProps
  }: TextAreaProps,
  ref: Ref<HTMLTextAreaElement | null>
): ReactElement {
  const [touched, setTouched] = useState(false);
  const [focused, setFocused] = useState(false);

  // get reference to textarea element to check invalid state
  // that can be passed up to the wrapper div
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  // associate the forwarded ref with the internal reference
  // to the textarea element
  useImperativeHandle(ref, () => textAreaRef.current!, []);

  const isInvalid =
    invalid || (!textAreaRef.current?.checkValidity() && touched);

  return (
    <FormField hint={hint} invalid={isInvalid} label={label} help={help}>
      <S.Wrapper
        $invalid={isInvalid}
        $border={border}
        $disabled={restProps.disabled}
        $focused={focused}
        className={className}
        style={style}
      >
        <S.TextArea
          {...restProps}
          ref={textAreaRef}
          onChange={(e) => onChange?.(e.target.value)}
          onFocus={(e) => {
            setFocused(true);
            restProps.onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            setTouched(true);
            restProps.onBlur?.(e);
          }}
        />
      </S.Wrapper>
    </FormField>
  );
});

interface FormTextAreaFieldProps<
  T extends FieldValues,
  K extends FieldPath<T>,
> extends Omit<TextAreaProps, "invalid" | "name" | "hint"> {
  rules?: RegisterOptions<T, K>;
  name: K;
  control: Control<T, K>;
}

/**
 * DRY textarea field component for use with react-hook-form.
 */
export function FormTextAreaField<
  T extends FieldValues,
  K extends FieldPath<T>,
>(props: FormTextAreaFieldProps<T, K>) {
  const { name, control, rules, ...rest } = props;

  return (
    <Controller
      control={control}
      rules={rules}
      name={name}
      render={({ field, fieldState }) => (
        <TextAreaField
          {...rest}
          {...field}
          value={field.value ?? ""}
          invalid={fieldState.invalid}
          hint={fieldState.error?.message}
        />
      )}
    />
  );
}
